import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageSquare, Plus, Edit2, Trash2, AlertTriangle, 
  AlertCircle, Info, Send, Clock, User, Mail, 
  ChevronRight, ArrowLeft, Reply, Users, CheckCheck, Check,
  UserMinus, UserPlus, Settings2, Archive, Paperclip, X, FileText, Image, File, Download,
  ZoomIn, ZoomOut, ChevronLeft, Maximize2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const Messages = () => {
  // Announcements state
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'normal',
    attachments: [],
    target_roles: []  // Empty means all roles
  });
  const announcementFileInputRef = useRef(null);
  const [readReceipts, setReadReceipts] = useState({}); // {messageId: {read_by: [], total_readers: 0}}
  const [expandedReadReceipts, setExpandedReadReceipts] = useState({}); // {messageId: boolean}
  
  // Direct Messages state
  const [conversations, setConversations] = useState([]);
  const [groupedRecipients, setGroupedRecipients] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [dmLoading, setDmLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newDmDialogOpen, setNewDmDialogOpen] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [messageableUsers, setMessageableUsers] = useState([]);
  const [dmFormData, setDmFormData] = useState({
    recipient_ids: [],
    subject: '',
    content: '',
    attachments: []
  });
  const [editingDm, setEditingDm] = useState(null);
  const [editDmContent, setEditDmContent] = useState('');
  const [sendingBulk, setSendingBulk] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const replyFileInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const isManager = user.role === 'manager';
  const canManageMessages = isAdmin || isManager;

  // Mailing list management state
  const [mailingListOpen, setMailingListOpen] = useState(false);
  const [duplicateNames, setDuplicateNames] = useState([]);
  const [excludedUsers, setExcludedUsers] = useState([]);
  const [loadingMailingList, setLoadingMailingList] = useState(false);

  // Archived conversations state
  const [archivedConversations, setArchivedConversations] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Lightbox state for image viewing
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxZoom, setLightboxZoom] = useState(1);

  useEffect(() => {
    fetchMessages();
    fetchConversations();
    if (canManageMessages) {
      fetchMessageableUsers();
    }
    if (isAdmin) {
      fetchArchivedConversations();
    }
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          if (lightboxImages.length > 1) prevImage();
          break;
        case 'ArrowRight':
          if (lightboxImages.length > 1) nextImage();
          break;
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
          zoomOut();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxImages.length]);

  // ==================== ANNOUNCEMENTS FUNCTIONS ====================
  
  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/messages`, getAuthHeader());
      setMessages(response.data);
      
      // For admin, fetch read receipts for all messages
      if (isAdmin && response.data.length > 0) {
        fetchAllReadReceipts(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllReadReceipts = async (messagesList) => {
    // Fetch read receipts for all messages in parallel
    const receiptsPromises = messagesList.map(async (message) => {
      try {
        const response = await axios.get(`${API}/messages/${message.id}/read-receipts`, getAuthHeader());
        return { messageId: message.id, data: response.data };
      } catch (error) {
        return { messageId: message.id, data: { read_by: [], total_readers: 0 } };
      }
    });
    
    const results = await Promise.all(receiptsPromises);
    const receiptsMap = {};
    results.forEach(result => {
      receiptsMap[result.messageId] = result.data;
    });
    setReadReceipts(receiptsMap);
  };

  const toggleReadReceipts = (messageId) => {
    setExpandedReadReceipts(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    try {
      if (editingMessage) {
        await axios.put(`${API}/messages/${editingMessage.id}`, formData, getAuthHeader());
        toast.success('Message updated successfully');
      } else {
        await axios.post(`${API}/messages`, formData, getAuthHeader());
        toast.success('Message posted successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save message');
    }
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setFormData({
      title: message.title,
      content: message.content,
      priority: message.priority,
      attachments: message.attachments || [],
      target_roles: message.target_roles || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      await axios.delete(`${API}/messages/${messageId}`, getAuthHeader());
      toast.success('Message deleted successfully');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  const resetForm = () => {
    setEditingMessage(null);
    setFormData({ title: '', content: '', priority: 'normal', attachments: [], target_roles: [] });
  };

  // ==================== DIRECT MESSAGES FUNCTIONS ====================
  
  const fetchConversations = async () => {
    try {
      setDmLoading(true);
      if (canManageMessages) {
        // Admin/Manager: fetch grouped by recipient
        const response = await axios.get(`${API}/direct-messages/grouped-by-recipient`, getAuthHeader());
        setGroupedRecipients(response.data);
      } else {
        // Staff: fetch regular conversations
        const response = await axios.get(`${API}/direct-messages/conversations`, getAuthHeader());
        setConversations(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setDmLoading(false);
    }
  };

  const fetchMessageableUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/messageable`, getAuthHeader());
      setMessageableUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch messageable users:', error);
    }
  };

  // ==================== MAILING LIST MANAGEMENT ====================
  
  const fetchMailingListData = async () => {
    if (!isAdmin) return;
    
    setLoadingMailingList(true);
    try {
      const [duplicatesRes, excludedRes] = await Promise.all([
        axios.get(`${API}/users/duplicate-names`, getAuthHeader()),
        axios.get(`${API}/users/messaging-excluded`, getAuthHeader())
      ]);
      setDuplicateNames(duplicatesRes.data);
      setExcludedUsers(excludedRes.data);
    } catch (error) {
      console.error('Failed to fetch mailing list data:', error);
      toast.error('Failed to load mailing list data');
    } finally {
      setLoadingMailingList(false);
    }
  };

  const handleExcludeUser = async (userId, userName) => {
    if (!window.confirm(`Remove "${userName}" from mailing list? Their conversations will be archived.`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/users/${userId}/exclude-from-messaging`, {}, getAuthHeader());
      toast.success(response.data.message);
      fetchMailingListData();
      fetchMessageableUsers();
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user from mailing list');
    }
  };

  const handleRestoreUser = async (userId, userName) => {
    try {
      const response = await axios.post(`${API}/users/${userId}/restore-to-messaging`, {}, getAuthHeader());
      toast.success(response.data.message);
      fetchMailingListData();
      fetchMessageableUsers();
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore user to mailing list');
    }
  };

  const openMailingListDialog = () => {
    fetchMailingListData();
    setMailingListOpen(true);
  };

  // ==================== ARCHIVED CONVERSATIONS ====================

  const fetchArchivedConversations = async () => {
    if (!isAdmin) return;
    
    setLoadingArchived(true);
    try {
      const response = await axios.get(`${API}/direct-messages/archived`, getAuthHeader());
      setArchivedConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch archived conversations:', error);
    } finally {
      setLoadingArchived(false);
    }
  };

  const handleArchiveConversation = async (conversationId) => {
    if (!window.confirm('Archive this conversation? It will be moved to the Archived tab.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/direct-messages/conversations/${conversationId}/archive`, {}, getAuthHeader());
      toast.success('Conversation archived');
      fetchConversations();
      fetchArchivedConversations();
      setSelectedConversation(null);
      setSelectedRecipient(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive conversation');
    }
  };

  const handleUnarchiveConversation = async (conversationId) => {
    try {
      await axios.post(`${API}/direct-messages/conversations/${conversationId}/unarchive`, {}, getAuthHeader());
      toast.success('Conversation restored');
      fetchConversations();
      fetchArchivedConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore conversation');
    }
  };

  const openConversation = async (conversation) => {
    try {
      const response = await axios.get(`${API}/direct-messages/conversations/${conversation.id}`, getAuthHeader());
      setSelectedConversation(response.data.conversation);
      setConversationMessages(response.data.messages);
      // Refresh conversation list to update unread counts
      fetchConversations();
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };

  // ==================== FILE ATTACHMENT HELPERS ====================
  
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_FILE_TYPES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'application/pdf',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain', 'text/csv'
  ];

  const handleFileSelect = async (e, type = 'dm') => {
    // type can be: 'dm', 'reply', 'announcement'
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validFiles = [];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Max 5MB allowed.`);
        continue;
      }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        toast.error(`${file.name} type not supported.`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    // Convert to base64
    const attachments = await Promise.all(validFiles.map(async (file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            filename: file.name,
            content_type: file.type,
            data: reader.result.split(',')[1], // Remove data:xxx;base64, prefix
            size: file.size
          });
        };
        reader.readAsDataURL(file);
      });
    }));

    if (type === 'reply') {
      setReplyAttachments(prev => [...prev, ...attachments].slice(0, 5)); // Max 5 files
    } else if (type === 'announcement') {
      setFormData(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...attachments].slice(0, 5)
      }));
    } else {
      setDmFormData(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...attachments].slice(0, 5)
      }));
    }

    // Reset input
    e.target.value = '';
  };

  const removeAttachment = (index, type = 'dm') => {
    if (type === 'reply') {
      setReplyAttachments(prev => prev.filter((_, i) => i !== index));
    } else if (type === 'announcement') {
      setFormData(prev => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index)
      }));
    } else {
      setDmFormData(prev => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index)
      }));
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <Image className="w-4 h-4" />;
    if (contentType === 'application/pdf') return <FileText className="w-4 h-4 text-red-500" />;
    return <File className="w-4 h-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const downloadAttachment = (attachment) => {
    const link = document.createElement('a');
    link.href = `data:${attachment.content_type};base64,${attachment.data}`;
    link.download = attachment.filename;
    link.click();
  };

  // Attachment preview component (for composing messages)
  const AttachmentPreview = ({ attachments, onRemove, type = 'dm' }) => {
    if (!attachments?.length) return null;
    
    const isImage = (contentType) => contentType?.startsWith('image/');
    
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {attachments.map((att, idx) => (
          <div key={idx} className="relative group">
            {isImage(att.content_type) ? (
              // Image thumbnail preview
              <div className="relative">
                <img
                  src={`data:${att.content_type};base64,${att.data}`}
                  alt={att.filename}
                  className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                />
                {onRemove && (
                  <button
                    type="button"
                    onClick={() => onRemove(idx, type)}
                    className="absolute -top-2 -right-2 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-sm"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
                <p className="text-[10px] text-slate-500 mt-0.5 max-w-[64px] truncate text-center">{att.filename}</p>
              </div>
            ) : (
              // Regular file preview
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg text-sm">
                {getFileIcon(att.content_type)}
                <span className="max-w-[120px] truncate">{att.filename}</span>
                <span className="text-slate-400 text-xs">({formatFileSize(att.size)})</span>
                {onRemove && (
                  <button
                    type="button"
                    onClick={() => onRemove(idx, type)}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ==================== LIGHTBOX FUNCTIONS ====================
  
  const openLightbox = (images, startIndex = 0) => {
    setLightboxImages(images);
    setLightboxIndex(startIndex);
    setLightboxZoom(1);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImages([]);
    setLightboxIndex(0);
    setLightboxZoom(1);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
    setLightboxZoom(1);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
    setLightboxZoom(1);
  };

  const zoomIn = () => setLightboxZoom((prev) => Math.min(prev + 0.25, 3));
  const zoomOut = () => setLightboxZoom((prev) => Math.max(prev - 0.25, 0.5));

  // Lightbox Component
  const ImageLightbox = () => {
    if (!lightboxOpen || lightboxImages.length === 0) return null;
    
    const currentImage = lightboxImages[lightboxIndex];
    
    return (
      <div 
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
        onClick={closeLightbox}
        data-testid="image-lightbox"
      >
        {/* Top Bar */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between bg-gradient-to-b from-black/50 to-transparent">
          <div className="text-white">
            <p className="font-medium">{currentImage.filename}</p>
            {lightboxImages.length > 1 && (
              <p className="text-sm text-white/70">{lightboxIndex + 1} of {lightboxImages.length}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); zoomOut(); }}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              title="Zoom Out"
            >
              <ZoomOut className="w-5 h-5" />
            </button>
            <span className="text-white text-sm min-w-[50px] text-center">{Math.round(lightboxZoom * 100)}%</span>
            <button
              onClick={(e) => { e.stopPropagation(); zoomIn(); }}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              title="Zoom In"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); downloadAttachment(currentImage); }}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              title="Download"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={closeLightbox}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation Arrows */}
        {lightboxImages.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              title="Previous"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              title="Next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Image Container */}
        <div 
          className="max-w-[90vw] max-h-[85vh] overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={`data:${currentImage.content_type};base64,${currentImage.data}`}
            alt={currentImage.filename}
            style={{ transform: `scale(${lightboxZoom})`, transformOrigin: 'center center' }}
            className="max-w-full max-h-[85vh] object-contain transition-transform duration-200"
          />
        </div>

        {/* Thumbnail Strip (for multiple images) */}
        {lightboxImages.length > 1 && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/50 to-transparent">
            <div className="flex justify-center gap-2">
              {lightboxImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(idx); setLightboxZoom(1); }}
                  className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-colors ${
                    idx === lightboxIndex ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={`data:${img.content_type};base64,${img.data}`}
                    alt={img.filename}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Message attachment display component with image previews
  const MessageAttachments = ({ attachments }) => {
    if (!attachments?.length) return null;
    
    const isImage = (contentType) => contentType?.startsWith('image/');
    
    // Separate images and other files
    const images = attachments.filter(att => isImage(att.content_type));
    const files = attachments.filter(att => !isImage(att.content_type));
    
    return (
      <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
        {/* Image Thumbnails Grid */}
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {images.map((att, idx) => (
              <div key={`img-${idx}`} className="relative group">
                <button 
                  onClick={() => openLightbox(images, idx)}
                  className="block focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded-lg"
                >
                  <img
                    src={`data:${att.content_type};base64,${att.data}`}
                    alt={att.filename}
                    className="w-24 h-24 object-cover rounded-lg border border-slate-200 hover:border-emerald-400 transition-colors cursor-pointer"
                  />
                </button>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-lg transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 pointer-events-none">
                  <div className="p-1.5 bg-white/90 rounded-full">
                    <Maximize2 className="w-4 h-4 text-slate-700" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1 max-w-[96px] truncate text-center">{att.filename}</p>
              </div>
            ))}
          </div>
        )}
        
        {/* Other File Types */}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {files.map((att, idx) => (
              <button
                key={`file-${idx}`}
                onClick={() => downloadAttachment(att)}
                className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 rounded-lg text-sm transition-colors"
              >
                {getFileIcon(att.content_type)}
                <span className="max-w-[150px] truncate">{att.filename}</span>
                <Download className="w-3 h-3 text-slate-400" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleNewDmSubmit = async (e) => {
    e.preventDefault();
    
    if (!dmFormData.recipient_ids || dmFormData.recipient_ids.length === 0 || !dmFormData.subject.trim() || !dmFormData.content.trim()) {
      toast.error('Please select at least one recipient and fill in all fields');
      return;
    }

    try {
      setSendingBulk(true);
      
      if (dmFormData.recipient_ids.length === 1) {
        // Single recipient - use original endpoint
        const response = await axios.post(`${API}/direct-messages/conversations`, {
          recipient_id: dmFormData.recipient_ids[0],
          subject: dmFormData.subject,
          content: dmFormData.content,
          attachments: dmFormData.attachments || []
        }, getAuthHeader());
        toast.success('Message sent successfully');
        setNewDmDialogOpen(false);
        setDmFormData({ recipient_ids: [], subject: '', content: '', attachments: [] });
        fetchConversations();
        // Open the newly created conversation
        openConversation(response.data.conversation);
      } else {
        // Multiple recipients - use bulk endpoint
        const response = await axios.post(`${API}/direct-messages/bulk`, {
          recipient_ids: dmFormData.recipient_ids,
          subject: dmFormData.subject,
          content: dmFormData.content,
          attachments: dmFormData.attachments || []
        }, getAuthHeader());
        
        const { sent_count, failed_count } = response.data;
        if (failed_count > 0) {
          toast.success(`Message sent to ${sent_count} recipient(s). ${failed_count} failed.`);
        } else {
          toast.success(`Message sent to ${sent_count} recipient(s)`);
        }
        setNewDmDialogOpen(false);
        setDmFormData({ recipient_ids: [], subject: '', content: '', attachments: [] });
        fetchConversations();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingBulk(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim() && replyAttachments.length === 0) {
      toast.error('Please enter a reply or attach a file');
      return;
    }

    try {
      await axios.post(
        `${API}/direct-messages/conversations/${selectedConversation.id}/reply`,
        { content: replyContent || '(Attachment)', attachments: replyAttachments },
        getAuthHeader()
      );
      setReplyContent('');
      setReplyAttachments([]);
      // Refresh conversation
      openConversation(selectedConversation);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    }
  };

  const handleEditDm = (message) => {
    setEditingDm(message);
    setEditDmContent(message.content);
  };

  const handleSaveEditDm = async () => {
    try {
      await axios.put(
        `${API}/direct-messages/messages/${editingDm.id}`,
        { content: editDmContent },
        getAuthHeader()
      );
      toast.success('Message updated');
      setEditingDm(null);
      setEditDmContent('');
      openConversation(selectedConversation);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update message');
    }
  };

  const handleDeleteDm = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      await axios.delete(`${API}/direct-messages/messages/${messageId}`, getAuthHeader());
      toast.success('Message deleted');
      openConversation(selectedConversation);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm('Delete this entire conversation? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/direct-messages/conversations/${conversationId}`, getAuthHeader());
      toast.success('Conversation deleted');
      setSelectedConversation(null);
      setConversationMessages([]);
      fetchConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete conversation');
    }
  };

  // ==================== HELPER FUNCTIONS ====================

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'important':
        return <AlertCircle className="w-4 h-4 text-amber-500" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      urgent: 'bg-red-100 text-red-700 border-red-200',
      important: 'bg-amber-100 text-amber-700 border-amber-200',
      normal: 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[priority] || styles.normal}`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      timeZone: 'Africa/Lagos',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { timeZone: 'Africa/Lagos', hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { timeZone: 'Africa/Lagos', weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { timeZone: 'Africa/Lagos', month: 'short', day: 'numeric' });
    }
  };

  const getRoleBadge = (role) => {
    const roleStyles = {
      staff: 'bg-emerald-100 text-emerald-700',
      vet_doctor: 'bg-pink-100 text-pink-700',
      distributor: 'bg-purple-100 text-purple-700',
      support_staff: 'bg-teal-100 text-teal-700',
      customer: 'bg-blue-100 text-blue-700'
    };
    const roleLabels = {
      staff: 'Staff',
      vet_doctor: 'Vet Doctor',
      distributor: 'Distributor',
      support_staff: 'Support Staff',
      customer: 'Customer'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleStyles[role] || 'bg-slate-100 text-slate-700'}`}>
        {roleLabels[role] || role}
      </span>
    );
  };

  // Group users by role for better organization
  const groupUsersByRole = (users) => {
    const groups = {
      staff: [],
      vet_doctor: [],
      distributor: [],
      support_staff: [],
      customer: []
    };
    
    users.forEach(u => {
      if (groups[u.role]) {
        groups[u.role].push(u);
      }
    });
    
    return groups;
  };

  const groupedMessageableUsers = groupUsersByRole(messageableUsers);

  // Get users by role type for quick selection
  const selectUsersByRole = (role) => {
    const usersOfRole = messageableUsers.filter(u => u.role === role).map(u => u.id);
    const currentIds = dmFormData.recipient_ids;
    const allSelected = usersOfRole.every(id => currentIds.includes(id));
    
    if (allSelected) {
      // Deselect all of this role
      setDmFormData({
        ...dmFormData,
        recipient_ids: currentIds.filter(id => !usersOfRole.includes(id))
      });
    } else {
      // Select all of this role
      const newIds = [...new Set([...currentIds, ...usersOfRole])];
      setDmFormData({ ...dmFormData, recipient_ids: newIds });
    }
  };

  return (
    <div className="space-y-6" data-testid="messages-page">
      {/* Image Lightbox */}
      <ImageLightbox />
      
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Messages
        </h1>
        <p className="text-slate-600 mt-1">
          {canManageMessages ? 'Manage announcements and direct messages' : 'View announcements and messages'}
        </p>
      </div>

      <Tabs defaultValue="announcements" className="space-y-4">
        <TabsList>
          <TabsTrigger value="announcements" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Announcements
          </TabsTrigger>
          <TabsTrigger value="direct" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Direct Messages
            {canManageMessages ? (
              // Admin/Manager: count from grouped recipients
              groupedRecipients.reduce((sum, r) => sum + r.total_unread, 0) > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-red-500 text-white rounded-full">
                  {groupedRecipients.reduce((sum, r) => sum + r.total_unread, 0)}
                </span>
              )
            ) : (
              // Staff: count from conversations
              conversations.filter(c => c.unread_count > 0).length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-red-500 text-white rounded-full">
                  {conversations.filter(c => c.unread_count > 0).length}
                </span>
              )
            )}
          </TabsTrigger>
        </TabsList>

        {/* ==================== ANNOUNCEMENTS TAB ==================== */}
        <TabsContent value="announcements" className="space-y-4">
          {canManageMessages && (
            <div className="flex justify-end">
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-message-button">
                    <Plus className="w-4 h-4 mr-2" /> New Announcement
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-emerald-600" />
                      {editingMessage ? 'Edit Announcement' : 'Post New Announcement'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label>Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Announcement title"
                        data-testid="message-title-input"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label>Priority</Label>
                      <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                        <SelectTrigger data-testid="message-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">
                            <div className="flex items-center gap-2">
                              <Info className="w-4 h-4 text-blue-500" />
                              Normal
                            </div>
                          </SelectItem>
                          <SelectItem value="important">
                            <div className="flex items-center gap-2">
                              <AlertCircle className="w-4 h-4 text-amber-500" />
                              Important
                            </div>
                          </SelectItem>
                          <SelectItem value="urgent">
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                              Urgent
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Target Audience</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {[
                          { id: '', label: 'Everyone', color: 'emerald' },
                          { id: 'staff', label: 'Staff', color: 'blue' },
                          { id: 'distributor', label: 'Distributors', color: 'purple' },
                          { id: 'customer', label: 'Customers', color: 'amber' },
                          { id: 'vet_doctor', label: 'Vet Doctors', color: 'pink' },
                          { id: 'support_staff', label: 'Support Staff', color: 'teal' }
                        ].map((role) => {
                          const isAll = role.id === '';
                          const isSelected = isAll 
                            ? formData.target_roles.length === 0 
                            : formData.target_roles.includes(role.id);
                          
                          return (
                            <button
                              key={role.id || 'all'}
                              type="button"
                              onClick={() => {
                                if (isAll) {
                                  setFormData({ ...formData, target_roles: [] });
                                } else {
                                  const current = formData.target_roles || [];
                                  if (current.includes(role.id)) {
                                    setFormData({ ...formData, target_roles: current.filter(r => r !== role.id) });
                                  } else {
                                    setFormData({ ...formData, target_roles: [...current, role.id] });
                                  }
                                }
                              }}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                isSelected 
                                  ? `bg-${role.color}-100 text-${role.color}-700 ring-2 ring-${role.color}-500` 
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                              style={isSelected ? {
                                backgroundColor: role.color === 'emerald' ? '#d1fae5' : 
                                                 role.color === 'blue' ? '#dbeafe' :
                                                 role.color === 'purple' ? '#ede9fe' :
                                                 role.color === 'amber' ? '#fef3c7' :
                                                 role.color === 'pink' ? '#fce7f3' :
                                                 role.color === 'teal' ? '#ccfbf1' : '#f1f5f9',
                                color: role.color === 'emerald' ? '#047857' : 
                                       role.color === 'blue' ? '#1d4ed8' :
                                       role.color === 'purple' ? '#6d28d9' :
                                       role.color === 'amber' ? '#b45309' :
                                       role.color === 'pink' ? '#be185d' :
                                       role.color === 'teal' ? '#0d9488' : '#475569',
                                boxShadow: '0 0 0 2px currentColor'
                              } : {}}
                            >
                              {role.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {formData.target_roles.length === 0 
                          ? 'This announcement will be visible to everyone' 
                          : `Visible to: ${formData.target_roles.map(r => r.replace('_', ' ')).join(', ')}`}
                      </p>
                    </div>
                    
                    <div>
                      <Label>Message Content *</Label>
                      <Textarea
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        placeholder="Write your announcement here..."
                        rows={5}
                        data-testid="message-content-input"
                        required
                      />
                    </div>
                    
                    {/* Attachments Section */}
                    <div>
                      <Label className="flex items-center gap-2">
                        <Paperclip className="w-4 h-4" />
                        Attachments (Optional)
                      </Label>
                      <div className="mt-1">
                        <input
                          type="file"
                          ref={announcementFileInputRef}
                          onChange={(e) => handleFileSelect(e, 'announcement')}
                          multiple
                          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => announcementFileInputRef.current?.click()}
                          disabled={(formData.attachments?.length || 0) >= 5}
                        >
                          <Paperclip className="w-4 h-4 mr-2" />
                          Add Files
                        </Button>
                        <span className="text-xs text-slate-500 ml-2">
                          Max 5 files, 5MB each
                        </span>
                      </div>
                      <AttachmentPreview 
                        attachments={formData.attachments} 
                        onRemove={removeAttachment}
                        type="announcement"
                      />
                    </div>
                    
                    <div className="flex gap-2 justify-end pt-2">
                      <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-message-button">
                        <Send className="w-4 h-4 mr-2" />
                        {editingMessage ? 'Update' : 'Post Announcement'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Messages List */}
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-12 text-slate-500">Loading announcements...</div>
            ) : messages.length === 0 ? (
              <div className="card p-12 text-center">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No Announcements Yet</h3>
                <p className="text-slate-500 mt-2">
                  {canManageMessages ? 'Post your first announcement using the button above.' : 'No announcements from management yet.'}
                </p>
              </div>
            ) : (
              messages.map((message) => (
                <div 
                  key={message.id} 
                  className={`card p-5 border-l-4 ${
                    message.priority === 'urgent' ? 'border-l-red-500 bg-red-50/30' :
                    message.priority === 'important' ? 'border-l-amber-500 bg-amber-50/30' :
                    'border-l-blue-500'
                  }`}
                  data-testid={`message-card-${message.id}`}
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getPriorityIcon(message.priority)}
                        <h3 className="font-semibold text-slate-900">{message.title}</h3>
                        {getPriorityBadge(message.priority)}
                        {!message.is_active && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                            Inactive
                          </span>
                        )}
                      </div>
                      
                      <p className="text-slate-700 whitespace-pre-wrap mb-4">{message.content}</p>
                      {/* Display message attachments */}
                      <MessageAttachments attachments={message.attachments} />
                      
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {message.created_by_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDate(message.created_at)}
                        </span>
                        {message.updated_at && (
                          <span className="text-slate-400">(edited)</span>
                        )}
                      </div>
                      
                      {/* Read Receipts - Admin Only */}
                      {isAdmin && readReceipts[message.id] && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <button
                            onClick={() => toggleReadReceipts(message.id)}
                            className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                            data-testid={`read-receipts-toggle-${message.id}`}
                          >
                            <CheckCheck className="w-4 h-4" />
                            <span>
                              Read by {readReceipts[message.id].total_readers} user{readReceipts[message.id].total_readers !== 1 ? 's' : ''}
                            </span>
                            <ChevronRight className={`w-4 h-4 transition-transform ${expandedReadReceipts[message.id] ? 'rotate-90' : ''}`} />
                          </button>
                          
                          {expandedReadReceipts[message.id] && (
                            <div className="mt-3 bg-slate-50 rounded-lg p-3" data-testid={`read-receipts-list-${message.id}`}>
                              {readReceipts[message.id].read_by.length === 0 ? (
                                <p className="text-sm text-slate-500 italic">No one has read this announcement yet</p>
                              ) : (
                                <div className="space-y-2">
                                  {readReceipts[message.id].read_by.map((reader, idx) => (
                                    <div key={idx} className="flex items-center justify-between text-sm">
                                      <div className="flex items-center gap-2">
                                        <User className="w-3 h-3 text-slate-400" />
                                        <span className="font-medium text-slate-700">{reader.name}</span>
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 capitalize">
                                          {reader.role?.replace('_', ' ')}
                                        </span>
                                      </div>
                                      <span className="text-xs text-slate-400">
                                        {reader.read_at ? formatDate(reader.read_at) : ''}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {canManageMessages && (
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleEdit(message)}
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDelete(message.id)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* ==================== DIRECT MESSAGES TAB ==================== */}
        <TabsContent value="direct" className="space-y-4">
          {selectedConversation ? (
            // Conversation Thread View
            <div className="card">
              {/* Conversation Header */}
              <div className="p-4 border-b flex items-center justify-between bg-slate-50">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" onClick={() => { 
                    setSelectedConversation(null); 
                    setConversationMessages([]); 
                  }}>
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div>
                    <h3 className="font-semibold text-slate-900">{selectedConversation.subject}</h3>
                    <p className="text-sm text-slate-500">
                      {canManageMessages 
                        ? `To: ${selectedConversation.recipient_name}` 
                        : `From: ${selectedConversation.initiated_by_name}`
                      }
                      {' '}
                      {getRoleBadge(selectedConversation.recipient_role)}
                    </p>
                  </div>
                </div>
                {canManageMessages && (
                  <div className="flex items-center gap-1">
                    {isAdmin && !showArchived && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleArchiveConversation(selectedConversation.id)}
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        title="Archive conversation"
                      >
                        <Archive className="w-4 h-4" />
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDeleteConversation(selectedConversation.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete conversation"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className="p-4 space-y-4 max-h-[400px] overflow-y-auto">
                {conversationMessages.map((msg) => {
                  const isOwnMessage = msg.sender_id === user.id;
                  return (
                    <div 
                      key={msg.id} 
                      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[75%] ${isOwnMessage ? 'bg-emerald-100' : 'bg-slate-100'} rounded-lg p-3`}>
                        {editingDm?.id === msg.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editDmContent}
                              onChange={(e) => setEditDmContent(e.target.value)}
                              rows={3}
                              className="text-sm"
                            />
                            <div className="flex gap-2 justify-end">
                              <Button size="sm" variant="outline" onClick={() => setEditingDm(null)}>
                                Cancel
                              </Button>
                              <Button size="sm" onClick={handleSaveEditDm} className="bg-emerald-600 hover:bg-emerald-700">
                                Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.content}</p>
                            {/* Display message attachments */}
                            <MessageAttachments attachments={msg.attachments} />
                            <div className="flex items-center justify-between mt-2">
                              <p className="text-xs text-slate-500">
                                {msg.sender_name} • {formatDate(msg.created_at)}
                                {msg.updated_at && ' (edited)'}
                              </p>
                              <div className="flex items-center gap-1">
                                {/* Read indicator for sender's own messages */}
                                {isOwnMessage && canManageMessages && (
                                  <span className={`flex items-center gap-0.5 text-xs ${msg.is_read ? 'text-blue-500' : 'text-slate-400'}`} title={msg.is_read ? `Read${msg.read_at ? ' at ' + formatDate(msg.read_at) : ''}` : 'Sent'}>
                                    {msg.is_read ? (
                                      <>
                                        <CheckCheck className="w-3.5 h-3.5" />
                                        <span className="text-[10px]">Read</span>
                                      </>
                                    ) : (
                                      <Check className="w-3.5 h-3.5" />
                                    )}
                                  </span>
                                )}
                                {isOwnMessage && (
                                  <div className="flex gap-1 ml-1">
                                    <button 
                                      onClick={() => handleEditDm(msg)}
                                      className="p-1 text-slate-400 hover:text-blue-600"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </button>
                                    <button 
                                      onClick={() => handleDeleteDm(msg.id)}
                                      className="p-1 text-slate-400 hover:text-red-600"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t bg-slate-50">
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Type your reply..."
                      rows={2}
                      className="flex-1"
                      data-testid="reply-input"
                    />
                    <div className="flex flex-col gap-1">
                      <input
                        type="file"
                        ref={replyFileInputRef}
                        onChange={(e) => handleFileSelect(e, true)}
                        multiple
                        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => replyFileInputRef.current?.click()}
                        disabled={replyAttachments.length >= 5}
                        title="Attach files"
                      >
                        <Paperclip className="w-4 h-4" />
                      </Button>
                      <Button 
                        onClick={handleReply}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        disabled={!replyContent.trim() && replyAttachments.length === 0}
                        data-testid="send-reply-button"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <AttachmentPreview 
                    attachments={replyAttachments} 
                    onRemove={removeAttachment}
                    type="reply"
                  />
                </div>
              </div>
            </div>
          ) : canManageMessages && selectedRecipient ? (
            // Recipient's Conversations List View (Admin only)
            <div className="card">
              {/* Recipient Header */}
              <div className="p-4 border-b flex items-center justify-between bg-slate-50">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedRecipient(null)}>
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div>
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <User className="w-5 h-5 text-emerald-600" />
                      {selectedRecipient.recipient_name}
                    </h3>
                    <p className="text-sm text-slate-500 flex items-center gap-2">
                      {getRoleBadge(selectedRecipient.recipient_role)}
                      <span>• {selectedRecipient.conversations.length} conversation(s)</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Conversations for this recipient */}
              <div className="divide-y">
                {selectedRecipient.conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => openConversation(conv)}
                    className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                      conv.unread_count > 0 ? 'bg-emerald-50/50' : ''
                    }`}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-semibold text-slate-900 ${conv.unread_count > 0 ? 'font-bold' : ''}`}>
                            {conv.subject}
                          </span>
                          {conv.unread_count > 0 && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-emerald-500 text-white rounded-full">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">
                          {conv.last_message_preview}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <span className="text-xs text-slate-400">{formatShortDate(conv.last_message_at)}</span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Main List View
            <>
              {canManageMessages && (
                <div className="flex justify-between items-center gap-2">
                  {/* Archived toggle for Admin */}
                  {isAdmin && (
                    <button
                      onClick={() => setShowArchived(!showArchived)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        showArchived 
                          ? 'bg-amber-100 text-amber-700' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <Archive className="w-4 h-4" />
                      {showArchived ? 'Viewing Archived' : `Archived (${archivedConversations.length})`}
                    </button>
                  )}
                  
                  <div className="flex gap-2 ml-auto">
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        onClick={openMailingListDialog}
                        data-testid="manage-mailing-list-button"
                      >
                        <Settings2 className="w-4 h-4 mr-2" /> Manage Mailing List
                      </Button>
                    )}
                    {!showArchived && (
                      <Dialog open={newDmDialogOpen} onOpenChange={setNewDmDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-dm-button">
                            <Plus className="w-4 h-4 mr-2" /> New Message
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                              <Mail className="w-5 h-5 text-emerald-600" />
                              Send Direct Message
                            </DialogTitle>
                          </DialogHeader>
                          <form onSubmit={handleNewDmSubmit} className="space-y-4">
                            <div>
                              <Label className="flex items-center justify-between">
                                <span>Recipients * {dmFormData.recipient_ids.length > 0 && `(${dmFormData.recipient_ids.length} selected)`}</span>
                                {messageableUsers.length > 0 && (
                                  <button
                                    type="button"
                                    className="text-xs text-emerald-600 hover:text-emerald-700"
                                    onClick={() => {
                                      if (dmFormData.recipient_ids.length === messageableUsers.length) {
                                        setDmFormData({ ...dmFormData, recipient_ids: [] });
                                      } else {
                                        setDmFormData({ ...dmFormData, recipient_ids: messageableUsers.map(u => u.id) });
                                      }
                                    }}
                              >
                                {dmFormData.recipient_ids.length === messageableUsers.length ? 'Deselect All' : 'Select All'}
                              </button>
                            )}
                          </Label>
                          <div className="border rounded-lg max-h-64 overflow-y-auto mt-2">
                            {messageableUsers.length === 0 ? (
                              <div className="p-4 text-center text-slate-500 text-sm">
                                No users available to message
                              </div>
                            ) : (
                              <>
                                {/* Staff Section */}
                                {groupedMessageableUsers.staff.length > 0 && (
                                  <div>
                                    <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 border-b sticky top-0">
                                      <span className="text-sm font-semibold text-emerald-700 flex items-center gap-2">
                                        <Users className="w-4 h-4" />
                                        Staff ({groupedMessageableUsers.staff.length})
                                      </span>
                                      <button
                                        type="button"
                                        className="text-xs text-emerald-600 hover:text-emerald-800"
                                        onClick={() => selectUsersByRole('staff')}
                                      >
                                        {groupedMessageableUsers.staff.every(u => dmFormData.recipient_ids.includes(u.id)) ? 'Deselect' : 'Select All'}
                                      </button>
                                    </div>
                                    {groupedMessageableUsers.staff.map((u) => (
                                      <label
                                        key={u.id}
                                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 transition-colors ${
                                          dmFormData.recipient_ids.includes(u.id) ? 'bg-emerald-50/50' : ''
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={dmFormData.recipient_ids.includes(u.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setDmFormData({ ...dmFormData, recipient_ids: [...dmFormData.recipient_ids, u.id] });
                                            } else {
                                              setDmFormData({ ...dmFormData, recipient_ids: dmFormData.recipient_ids.filter(id => id !== u.id) });
                                            }
                                          }}
                                          className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                                        />
                                        <span className="font-medium text-slate-900">{u.name}</span>
                                      </label>
                                    ))}
                                  </div>
                                )}

                                {/* Distributors Section */}
                                {groupedMessageableUsers.distributor.length > 0 && (
                                  <div>
                                    <div className="flex items-center justify-between px-3 py-2 bg-purple-50 border-b sticky top-0">
                                      <span className="text-sm font-semibold text-purple-700 flex items-center gap-2">
                                        <Users className="w-4 h-4" />
                                        Distributors ({groupedMessageableUsers.distributor.length})
                                      </span>
                                      <button
                                        type="button"
                                        className="text-xs text-purple-600 hover:text-purple-800"
                                        onClick={() => selectUsersByRole('distributor')}
                                      >
                                        {groupedMessageableUsers.distributor.every(u => dmFormData.recipient_ids.includes(u.id)) ? 'Deselect' : 'Select All'}
                                      </button>
                                    </div>
                                    {groupedMessageableUsers.distributor.map((u) => (
                                      <label
                                        key={u.id}
                                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 transition-colors ${
                                          dmFormData.recipient_ids.includes(u.id) ? 'bg-purple-50/50' : ''
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={dmFormData.recipient_ids.includes(u.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setDmFormData({ ...dmFormData, recipient_ids: [...dmFormData.recipient_ids, u.id] });
                                            } else {
                                              setDmFormData({ ...dmFormData, recipient_ids: dmFormData.recipient_ids.filter(id => id !== u.id) });
                                            }
                                          }}
                                          className="w-4 h-4 text-purple-600 rounded border-slate-300 focus:ring-purple-500"
                                        />
                                        <span className="font-medium text-slate-900">{u.name}</span>
                                      </label>
                                    ))}
                                  </div>
                                )}

                                {/* Customers Section */}
                                {groupedMessageableUsers.customer.length > 0 && (
                                  <div>
                                    <div className="flex items-center justify-between px-3 py-2 bg-blue-50 border-b sticky top-0">
                                      <span className="text-sm font-semibold text-blue-700 flex items-center gap-2">
                                        <Users className="w-4 h-4" />
                                        Customers ({groupedMessageableUsers.customer.length})
                                      </span>
                                      <button
                                        type="button"
                                        className="text-xs text-blue-600 hover:text-blue-800"
                                        onClick={() => selectUsersByRole('customer')}
                                      >
                                        {groupedMessageableUsers.customer.every(u => dmFormData.recipient_ids.includes(u.id)) ? 'Deselect' : 'Select All'}
                                      </button>
                                    </div>
                                    {groupedMessageableUsers.customer.map((u) => (
                                      <label
                                        key={u.id}
                                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 transition-colors ${
                                          dmFormData.recipient_ids.includes(u.id) ? 'bg-blue-50/50' : ''
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={dmFormData.recipient_ids.includes(u.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setDmFormData({ ...dmFormData, recipient_ids: [...dmFormData.recipient_ids, u.id] });
                                            } else {
                                              setDmFormData({ ...dmFormData, recipient_ids: dmFormData.recipient_ids.filter(id => id !== u.id) });
                                            }
                                          }}
                                          className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                        />
                                        <div className="flex-1">
                                          <span className="font-medium text-slate-900">{u.name}</span>
                                          {u.affiliated_distributor_name && (
                                            <span className="ml-2 text-xs text-slate-500">
                                              ({u.affiliated_distributor_name})
                                            </span>
                                          )}
                                        </div>
                                      </label>
                                    ))}
                                  </div>
                                )}

                                {/* Vet Doctors Section */}
                                {groupedMessageableUsers.vet_doctor.length > 0 && (
                                  <div>
                                    <div className="flex items-center justify-between px-3 py-2 bg-pink-50 border-b sticky top-0">
                                      <span className="text-sm font-semibold text-pink-700 flex items-center gap-2">
                                        <Users className="w-4 h-4" />
                                        Vet Doctors ({groupedMessageableUsers.vet_doctor.length})
                                      </span>
                                      <button
                                        type="button"
                                        className="text-xs text-pink-600 hover:text-pink-800"
                                        onClick={() => selectUsersByRole('vet_doctor')}
                                      >
                                        {groupedMessageableUsers.vet_doctor.every(u => dmFormData.recipient_ids.includes(u.id)) ? 'Deselect' : 'Select All'}
                                      </button>
                                    </div>
                                    {groupedMessageableUsers.vet_doctor.map((u) => (
                                      <label
                                        key={u.id}
                                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 transition-colors ${
                                          dmFormData.recipient_ids.includes(u.id) ? 'bg-pink-50/50' : ''
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={dmFormData.recipient_ids.includes(u.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setDmFormData({ ...dmFormData, recipient_ids: [...dmFormData.recipient_ids, u.id] });
                                            } else {
                                              setDmFormData({ ...dmFormData, recipient_ids: dmFormData.recipient_ids.filter(id => id !== u.id) });
                                            }
                                          }}
                                          className="w-4 h-4 text-pink-600 rounded border-slate-300 focus:ring-pink-500"
                                        />
                                        <span className="font-medium text-slate-900">{u.name}</span>
                                      </label>
                                    ))}
                                  </div>
                                )}

                                {/* Support Staff Section */}
                                {groupedMessageableUsers.support_staff.length > 0 && (
                                  <div>
                                    <div className="flex items-center justify-between px-3 py-2 bg-teal-50 border-b sticky top-0">
                                      <span className="text-sm font-semibold text-teal-700 flex items-center gap-2">
                                        <Users className="w-4 h-4" />
                                        Support Staff ({groupedMessageableUsers.support_staff.length})
                                      </span>
                                      <button
                                        type="button"
                                        className="text-xs text-teal-600 hover:text-teal-800"
                                        onClick={() => selectUsersByRole('support_staff')}
                                      >
                                        {groupedMessageableUsers.support_staff.every(u => dmFormData.recipient_ids.includes(u.id)) ? 'Deselect' : 'Select All'}
                                      </button>
                                    </div>
                                    {groupedMessageableUsers.support_staff.map((u) => (
                                      <label
                                        key={u.id}
                                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 transition-colors ${
                                          dmFormData.recipient_ids.includes(u.id) ? 'bg-teal-50/50' : ''
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={dmFormData.recipient_ids.includes(u.id)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setDmFormData({ ...dmFormData, recipient_ids: [...dmFormData.recipient_ids, u.id] });
                                            } else {
                                              setDmFormData({ ...dmFormData, recipient_ids: dmFormData.recipient_ids.filter(id => id !== u.id) });
                                            }
                                          }}
                                          className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                                        />
                                        <span className="font-medium text-slate-900">{u.name}</span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <Label>Subject *</Label>
                          <Input
                            value={dmFormData.subject}
                            onChange={(e) => setDmFormData({ ...dmFormData, subject: e.target.value })}
                            placeholder="Message subject"
                            data-testid="dm-subject-input"
                            required
                          />
                        </div>
                        
                        <div>
                          <Label>Message *</Label>
                          <Textarea
                            value={dmFormData.content}
                            onChange={(e) => setDmFormData({ ...dmFormData, content: e.target.value })}
                            placeholder="Write your message..."
                            rows={4}
                            data-testid="dm-content-input"
                            required
                          />
                        </div>
                        
                        {/* Attachments Section */}
                        <div>
                          <Label className="flex items-center gap-2">
                            <Paperclip className="w-4 h-4" />
                            Attachments (Optional)
                          </Label>
                          <div className="mt-1">
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={(e) => handleFileSelect(e, false)}
                              multiple
                              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                              className="hidden"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={(dmFormData.attachments?.length || 0) >= 5}
                            >
                              <Paperclip className="w-4 h-4 mr-2" />
                              Add Files
                            </Button>
                            <span className="text-xs text-slate-500 ml-2">
                              Max 5 files, 5MB each (Images, PDF, Word, Excel, TXT)
                            </span>
                          </div>
                          <AttachmentPreview 
                            attachments={dmFormData.attachments} 
                            onRemove={removeAttachment}
                            type="dm"
                          />
                        </div>
                        
                        <div className="flex gap-2 justify-end pt-2">
                          <Button type="button" variant="outline" onClick={() => setNewDmDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button 
                            type="submit" 
                            className="bg-emerald-600 hover:bg-emerald-700" 
                            data-testid="send-dm-button"
                            disabled={sendingBulk || dmFormData.recipient_ids.length === 0}
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {sendingBulk ? 'Sending...' : dmFormData.recipient_ids.length > 1 
                              ? `Send to ${dmFormData.recipient_ids.length} Recipients` 
                              : 'Send Message'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                    )}
                  </div>
                </div>
              )}

              {/* Show Archived Conversations or Regular View */}
              {showArchived && isAdmin ? (
                // Archived Conversations View
                <div className="card divide-y">
                  <div className="p-3 bg-amber-50 border-b flex items-center gap-2">
                    <Archive className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-amber-800">Archived Conversations</span>
                    <span className="text-sm text-amber-600">({archivedConversations.length})</span>
                  </div>
                  {loadingArchived ? (
                    <div className="text-center py-12 text-slate-500">Loading archived...</div>
                  ) : archivedConversations.length === 0 ? (
                    <div className="p-12 text-center">
                      <Archive className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700">No Archived Conversations</h3>
                      <p className="text-slate-500 mt-2">
                        Archived conversations will appear here.
                      </p>
                    </div>
                  ) : (
                    archivedConversations.map((conv) => (
                      <div
                        key={conv.id}
                        className="p-4 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-slate-700">
                                {conv.subject}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                Archived
                              </span>
                            </div>
                            <p className="text-sm text-slate-500">
                              To: {conv.recipient_name} • {conv.archive_reason || 'Archived'}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              Archived: {formatDate(conv.archived_at)}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={() => handleUnarchiveConversation(conv.id)}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            Restore
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
              /* Grouped Recipients List (Admin/Manager) or Conversations List (Staff) */
              <div className="card divide-y">
                {dmLoading ? (
                  <div className="text-center py-12 text-slate-500">Loading messages...</div>
                ) : canManageMessages ? (
                  // Admin/Manager: Show recipients grouped
                  groupedRecipients.length === 0 ? (
                    <div className="p-12 text-center">
                      <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700">No Direct Messages</h3>
                      <p className="text-slate-500 mt-2">
                        Start a conversation by clicking &quot;New Message&quot; above.
                      </p>
                    </div>
                  ) : (
                    groupedRecipients.map((recipient) => (
                      <div
                        key={recipient.recipient_id}
                        onClick={() => setSelectedRecipient(recipient)}
                        className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                          recipient.total_unread > 0 ? 'bg-emerald-50/50' : ''
                        }`}
                        data-testid={`recipient-${recipient.recipient_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                              <User className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className={`font-semibold text-slate-900 ${recipient.total_unread > 0 ? 'font-bold' : ''}`}>
                                  {recipient.recipient_name}
                                </span>
                                {getRoleBadge(recipient.recipient_role)}
                                {recipient.total_unread > 0 && (
                                  <span className="px-1.5 py-0.5 text-xs font-medium bg-emerald-500 text-white rounded-full">
                                    {recipient.total_unread}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">
                                {recipient.conversations.length} conversation{recipient.conversations.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{formatShortDate(recipient.last_message_at)}</span>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      </div>
                    ))
                  )
                ) : (
                  // Staff: Show regular conversations
                  conversations.length === 0 ? (
                    <div className="p-12 text-center">
                      <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700">No Direct Messages</h3>
                      <p className="text-slate-500 mt-2">
                        You have no direct messages yet.
                      </p>
                    </div>
                  ) : (
                    conversations.map((conv) => (
                      <div
                        key={conv.id}
                        onClick={() => openConversation(conv)}
                        className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                          conv.unread_count > 0 ? 'bg-emerald-50/50' : ''
                        }`}
                        data-testid={`conversation-${conv.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`font-semibold text-slate-900 ${conv.unread_count > 0 ? 'font-bold' : ''}`}>
                                {conv.initiated_by_name}
                              </span>
                              {conv.unread_count > 0 && (
                                <span className="px-1.5 py-0.5 text-xs font-medium bg-emerald-500 text-white rounded-full">
                                  {conv.unread_count}
                                </span>
                              )}
                            </div>
                            <p className={`text-sm ${conv.unread_count > 0 ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                              {conv.subject}
                            </p>
                            <p className="text-sm text-slate-500 truncate mt-1">
                              {conv.last_message_preview}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <span className="text-xs text-slate-400">{formatShortDate(conv.last_message_at)}</span>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      </div>
                    ))
                  )
                )}
              </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Manage Mailing List Dialog */}
      <Dialog open={mailingListOpen} onOpenChange={setMailingListOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-amber-600" />
              Manage Mailing List
            </DialogTitle>
          </DialogHeader>
          
          {loadingMailingList ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : (
            <div className="space-y-6">
              {/* Duplicate Names Section */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Duplicate Names ({duplicateNames.length})
                </h3>
                <p className="text-sm text-slate-500 mb-3">
                  These names appear multiple times in the mailing list. Remove duplicates to avoid confusion.
                </p>
                
                {duplicateNames.length === 0 ? (
                  <div className="text-center py-6 bg-emerald-50 rounded-lg text-emerald-700">
                    <CheckCheck className="w-8 h-8 mx-auto mb-2" />
                    No duplicate names found
                  </div>
                ) : (
                  <div className="space-y-3">
                    {duplicateNames.map((group, idx) => (
                      <div key={idx} className="border rounded-lg p-3 bg-amber-50/50">
                        <div className="font-medium text-slate-800 mb-2">
                          &quot;{group.name}&quot; <span className="text-amber-600">({group.count} users)</span>
                        </div>
                        <div className="space-y-2">
                          {group.users.map((u) => (
                            <div 
                              key={u.id} 
                              className={`flex items-center justify-between p-2 rounded ${
                                u.excluded_from_messaging ? 'bg-red-50' : 'bg-white'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                <span className={u.excluded_from_messaging ? 'line-through text-slate-400' : ''}>
                                  {u.name}
                                </span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">
                                  {u.role?.replace('_', ' ')}
                                </span>
                                <span className="text-xs text-slate-400">{u.email}</span>
                                {u.excluded_from_messaging && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                                    Removed
                                  </span>
                                )}
                              </div>
                              {u.excluded_from_messaging ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => handleRestoreUser(u.id, u.name)}
                                >
                                  <UserPlus className="w-4 h-4 mr-1" />
                                  Restore
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleExcludeUser(u.id, u.name)}
                                >
                                  <UserMinus className="w-4 h-4 mr-1" />
                                  Remove
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Excluded Users Section */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Archive className="w-4 h-4 text-slate-500" />
                  Removed from Mailing List ({excludedUsers.length})
                </h3>
                
                {excludedUsers.length === 0 ? (
                  <div className="text-center py-4 bg-slate-50 rounded-lg text-slate-500 text-sm">
                    No users have been removed from the mailing list
                  </div>
                ) : (
                  <div className="border rounded-lg divide-y">
                    {excludedUsers.map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span>{u.name}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">
                            {u.role?.replace('_', ' ')}
                          </span>
                          <span className="text-xs text-slate-400">{u.email}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleRestoreUser(u.id, u.name)}
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
